import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    GelScreenDetectorContext,
    GelRowLayout,
    GelContainerLite,
    GelParagraph,
    GelHeading5,
    GelHeading2,
    GelSpinner,
    GelButton,
    GelModal,
    GelLabel,
    GelForm,
    useGelFormData,
    GelStandardDateInput,
    GelTag
} from '@tal-gel/components';
import { UserContext } from '../../common/usercontext/user.context';
import { getGelTokens } from '@tal-gel/theming';
import Card from '../../common/components/card';
import ResponsiveContainer from "../../components/responsive.container";
import Flexbox from '../../common/components/FlexBox';
import Pagination from '../../common/components/Pagination';
import { HistoricalPaymentDetail, ScheduledPaymentDetail } from '../../common/models/memberclaims';
import PageHeader from '../../common/components/PageHeader';
import { PaymentItem } from './PaymentItem';
import Spacer from '../../common/components/Spacer';
import { distinctByBenefitGroupId, formatAsCurrency, formatDate, getGroupMembersByPaymentId, getPaginatedPayments, getSortedScheduledPayments } from './helpers';
import { useGetHomePageContent } from '../../common/api/graphQLDataFetch';
import HorizontalRule from '../../common/components/HorizontalRule';
import InlineAlert from '../../common/components/InlineAlert';
import { FilterActions, FilterBar, FilterChips } from './PaymentFilter';

const PaymentHistoryOverview = () => {
    const { global: { sizeBaseUnit, sizeBaseX10, sizeBaseX3, sizeNone,
        sizeBaseX5, sizeBaseX8, sizeBaseX4, sizeBaseX2 } } = getGelTokens();

    const { screen, isXsScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;

    const { contextData } = useContext(UserContext)
    const navigate = useNavigate();

    const { data, isLoading } = useGetHomePageContent();

    const [openModal, setOpenModal] = useState(false);
    const {
        formData,
        onFormDataChange,
        onFormDataReset,
        resetForm    
    } = useGelFormData({
        fromDate: '',
        toDate: ''
    });
    const onSubmit = () => {
        setOpenModal(false);
        setShowDateFilter(true);
    }
    const [showDateFilter, setShowDateFilter] = useState(false);
    const onResetFilter = () => {
        onFormDataChange({ target: { name: 'fromDate', value: '' } }, false)
        onFormDataChange({ target: { name: 'toDate', value: '' } }, false);
        setOpenModal(false);
        setShowDateFilter(false);
    }

    const isValidFilter = () => {
        return formData.fromDate && formData.toDate && new Date(formData.fromDate) <= new Date(formData.toDate);
    }

    let scheduled: ScheduledPaymentDetail[] = getSortedScheduledPayments(contextData.scheduledPayments);
    let historical: HistoricalPaymentDetail[] = distinctByBenefitGroupId(contextData.historicalPayments ?? []);

    const allDates = [
        ...historical.map(h => h.createdTS),
        ...scheduled.map(s => s.createdTS)
    ];

    // find the most recent timestamp, or undefined if no data
    const mostRecentActivity = allDates.length
        ? allDates.reduce((max, d) =>
            new Date(d) > new Date(max) ? d : max
            , allDates[0])
        : undefined;

    // If showDateFilter is true, filter historical payments by date range
    if (showDateFilter && formData.fromDate && formData.toDate) {
        const from = new Date(formData.fromDate);
        const to = new Date(formData.toDate);
        historical = historical.filter(p => {
            const paymentDate = new Date(p.paymentDate);
            return paymentDate >= from && paymentDate <= to;
        });
    }

    const [scheduledPageNumber, setScheduledPageNumber] = useState(1);
    const [historicalPageNumber, setHistoricalPageNumber] = useState(1);

    const paymentsPerPage = 4;
    function calculatePageCount<T>(items: T[], itemsPerPage: number): number {
        return Math.ceil(items.length / itemsPerPage);
    }

    // Calculate page count for scheduled and historical payments
    const scheduledPageCount = calculatePageCount(scheduled, paymentsPerPage);
    const historicalPageCount = calculatePageCount(historical, paymentsPerPage);

    // Get the remaining items to fill the page (based on the type)
    function getRemainderItems<T>(
        payments: T[],
        pageNumber: number,
        paymentsPerPage: number
    ): number[] {
        const startIndex = (pageNumber - 1) * paymentsPerPage;
        const endIndex = Math.min(startIndex + paymentsPerPage, payments.length);
        const remainder = paymentsPerPage - (endIndex - startIndex);
        return Array.from({ length: Math.max(0, remainder) }, (_, index) => index);
    }

    const onPaymentClick = (payment: HistoricalPaymentDetail) => {
        navigate(`/payment/${payment.paymentId}`);
    }

    if (isLoading) return <GelSpinner medium overlay />;

    return (<>
        {openModal &&
            <GelForm
                labelAtTop
                disableOnSubmit
                preventSubmitOnInvalid
                onSubmit={onSubmit}
                reset={resetForm}
                onReset={onFormDataReset}
                parseResponseBody={false}
            ><GelModal
                width="sm"
                open={openModal}
                onClose={() => setOpenModal(false)}
                closeOnBackdropClick={false}
                sticky
                title={'Filter'}
                action={
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <GelButton
                            name=""
                            secondary medium
                            onClick={onResetFilter}
                            width={sizeBaseUnit * 22}
                        >
                            Reset
                        </GelButton>
                        <GelButton
                            name=""
                            primary medium submit
                            width={sizeBaseUnit * 22}
                            onClick={onSubmit}
                            disabled={!isValidFilter()}
                        >
                            Apply
                        </GelButton>
                    </div>
                }>
                    <>
                        <GelLabel style={{ paddingBottom: '24px' }}>Date</GelLabel>

                        <div style={{ display: 'flex', width: '100%', gap: '24px' }}>
                            <div style={{ flex: 1 }}>
                                <GelParagraph>From</GelParagraph>

                                <GelStandardDateInput
                                    name="fromDate"
                                    value={formData.fromDate}
                                    onChange={onFormDataChange}
                                    required
                                    errorMsg={{
                                        required: 'This field is required',
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <GelParagraph>To</GelParagraph>
                                <GelStandardDateInput
                                    name="toDate"
                                    value={formData.toDate}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                        onFormDataChange(ev, false)
                                    }}
                                    required
                                    errorMsg={{
                                        required: 'This field is required',
                                    }}
                                />
                            </div>
                        </div>
                    </>
                </GelModal>
            </GelForm>}

        <GelContainerLite style={{ padding: sizeNone, overflow: "hidden" }}>
            <PageHeader >
                <GelHeading2>{data?.payments?.Heading}</GelHeading2>
                <GelParagraph style={{ paddingTop: sizeBaseX3 }}>{data?.payments?.SubHeading}</GelParagraph>
            </PageHeader>

            <ResponsiveContainer>
                <GelRowLayout style={{ padding: isXsScreen(screen) ? `${sizeBaseX10}px ${sizeBaseX5}px` : `${sizeBaseX5}px ${sizeBaseX10}px` }}>

                    {mostRecentActivity && <InlineAlert 
                        message={`${data?.payments?.LastUpdatedAlert} ${formatDate(mostRecentActivity, true)}`} 
                        variant={"info"} />}
                    
                    <Card title={data?.payments?.Scheduled?.Heading ?? ""}
                        subTitle={data?.payments?.Scheduled?.SubHeading}
                        toolTip={data?.payments?.Scheduled?.ToolTip}
                        showFooter={false}
                        lightHeader={false}>
                        <GelContainerLite style={{ padding: `${sizeBaseX8}px ${sizeBaseX4}px` }}>

                            <Flexbox gap={sizeBaseX2} flexDirection="column" style={scheduled.length === 0 ? { height: '384px' } : {}}>
                                {scheduled.length > 0 && getPaginatedPayments(scheduled, scheduledPageNumber, paymentsPerPage).map(p =>
                                    <PaymentItem
                                        key={p.scheduledPaymentId}
                                        paymentDate={formatDate(p.paymentDate)}
                                        amount={formatAsCurrency(p.amount)}
                                        text={`${p.claimType} - ${formatDate(p.dateFrom)} to ${formatDate(p.dateTo)}`}
                                        variant={"not-clickable"}
                                    />)}

                                {scheduled.length > 0 && getRemainderItems(scheduled, scheduledPageNumber, paymentsPerPage).map(idx => <PaymentItem key={`space-digicorro-${idx}`} variant={"hidden"} />)}

                                {scheduled.length === 0 && <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%, -50%)", width: "100%" }}>
                                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                                        <GelHeading5 style={{ paddingTop: "10%", color: "#54544C" }}>{data?.payments?.Scheduled?.NoPayments?.Message}</GelHeading5>
                                        <GelParagraph style={{ paddingTop: "8px", color: "#54544C", fontWeight: "400" }}>{data?.payments?.Scheduled?.NoPayments?.Description}</GelParagraph>
                                    </div>
                                </div>}
                            </Flexbox>

                            {scheduled.length > 0 &&
                                <>
                                    <Spacer />
                                    <Pagination page={scheduledPageNumber} count={scheduledPageCount} onChange={setScheduledPageNumber} />
                                </>
                            }

                        </GelContainerLite>
                    </Card>

                    <Card title={data?.payments?.Historical?.Heading ?? ""}
                        subTitle={data?.payments?.Historical?.SubHeading}
                        toolTip={data?.payments?.Historical?.ToolTip}
                        showFooter={false}
                        lightHeader={true}
                        style={{ marginBottom: '44px' }}>
                        <GelContainerLite style={{ padding: `${sizeBaseX8}px ${sizeBaseX4}px` }}>

                            <Flexbox gap={sizeBaseX2} flexDirection="column" style={historical.length === 0 ? { height: '384px' } : {}}>

                                {historical.length > 0 && <>
                                    <FilterBar>
                                        <FilterChips>
                                            {showDateFilter && <GelTag
                                                tagId="1"
                                                onDelete={onResetFilter}
                                                small
                                                icon="ExitSmall"
                                            >
                                                {`${formatDate(formData.fromDate)} to ${formatDate(formData.toDate)}`}
                                            </GelTag>
                                            }
                                        </FilterChips>
                                        <FilterActions>
                                            <GelButton
                                                primary medium
                                                iconRight="FilterFunnel"
                                                onClick={() => setOpenModal(true)}
                                                style={{ flex: 1 }}
                                            >Filter</GelButton>

                                            {showDateFilter && <GelButton
                                                tertiary medium
                                                onClick={onResetFilter}
                                                style={{ flex: 1 }}
                                            >Clear all filters</GelButton>}
                                        </FilterActions>
                                    </FilterBar>
                                    <HorizontalRule />
                                </>}

                                {historical.length > 0 && getPaginatedPayments(historical, historicalPageNumber, paymentsPerPage).map(p =>
                                    <PaymentItem
                                        key={p.paymentId}
                                        paymentDate={formatDate(p.paymentDate)}
                                        amount={formatAsCurrency(getGroupMembersByPaymentId(p.paymentId, historical).reduce((a,c) => a + c.netAmount, 0))}
                                        text={`${p.claimType} - ${formatDate(p.benefitPaymentDateFrom)} to ${formatDate(p.benefitPaymentDateTo)}`}
                                        variant={"clickable"}
                                        onClick={() => onPaymentClick(p)}
                                    />)}

                                {historical.length > 0 && getRemainderItems(historical, historicalPageNumber, paymentsPerPage).map(idx => <PaymentItem key={`space-payment-${idx}`} variant={"hidden"} />)}

                                {historical.length === 0 && <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%, -50%)", width: "100%" }}>
                                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                                        <GelHeading5 style={{ paddingTop: "10%", color: "#54544C" }}>{data?.payments?.Historical?.NoPayments?.Message}</GelHeading5>
                                        <GelParagraph style={{ paddingTop: "8px", color: "#54544C", fontWeight: "400" }}>{data?.payments?.Historical?.NoPayments?.Description}</GelParagraph>
                                    </div>
                                </div>}
                            </Flexbox>

                            {historical.length > 0 &&
                                <>
                                    <Spacer />
                                    <Pagination page={historicalPageNumber} count={historicalPageCount} onChange={setHistoricalPageNumber} />
                                </>
                            }

                        </GelContainerLite>
                    </Card>
                </GelRowLayout>
            </ResponsiveContainer>

        </GelContainerLite>
    </>
    );
}

export default PaymentHistoryOverview;